import { useEffect, useState } from "react";
import { request } from "../api/client";
import { AppSelect } from "../components/inputs/AppSelect";
import { useLocale } from "../i18n";

// Count the round's complete active inventory, never a filtered/current UI page.
export async function roundTeamCount(roundId: string, version: number, signal: AbortSignal): Promise<number> {
  const ids = new Set<string>(); const cursors = new Set<string>(); let cursor = "";
  for (let page = 0; page < 11; page++) {
    const data = await request<{ items: { entryId: string; status: string }[]; nextCursor?: string | null }>(
      "/events/eventEntries?" + new URLSearchParams({ roundId, status: "ACTIVE", limit: "100", ...(cursor ? { cursor } : {}) }), "GET", undefined, signal);
    if (!Array.isArray(data.items) || data.items.length > 100) throw new Error("Invalid round inventory");
    for (const entry of data.items) {
      if (typeof entry.entryId !== "string" || !entry.entryId || entry.status !== "ACTIVE" || ids.has(entry.entryId)) throw new Error("Round inventory changed");
      ids.add(entry.entryId);
    }
    if (ids.size > 1000) throw new Error("Round inventory exceeds supported size");
    const next = data.nextCursor;
    if (!next) {
      const current = await request<{ unitId: string; version: number }>("/events/eventUnit?" + new URLSearchParams({ unitId: roundId }), "GET", undefined, signal);
      if (current.unitId !== roundId || current.version !== version) throw new Error("Round inventory changed");
      return ids.size;
    }
    if (typeof next !== "string" || cursors.has(next) || !data.items.length) throw new Error("Round inventory changed");
    cursors.add(next); cursor = next;
  }
  throw new Error("Round inventory exceeds supported size");
}

export function useRoundTeamCount(roundId: string, version: number | undefined, revision: number) {
  const key = `${roundId}:${version}:${revision}`;
  const [state, setState] = useState<{ key: string; count?: number; error?: boolean }>();
  useEffect(() => {
    if (!roundId || version === undefined) return;
    const controller = new AbortController(); setState({ key });
    void roundTeamCount(roundId, version, controller.signal)
      .then(count => { if (!controller.signal.aborted) setState({ key, count }); })
      .catch(() => { if (!controller.signal.aborted) setState({ key, error: true }); });
    return () => controller.abort();
  }, [roundId, version, key]);
  return state?.key === key ? state : undefined;
}

export function RoundTeamLimit({ roleName, value, count, onChange }: { roleName: string; value: number; count?: number; onChange: (value: number) => void }) {
  const { w } = useLocale(); const [mode, setMode] = useState("number");
  // The existing API stores a numeric, frozen cap; “all” fills that cap using the
  // current round inventory. It never means unlimited or changes a published policy.
  const allAvailable = count !== undefined && count > 0 && count <= 100;
  return <div className="evaluation-team-limit">
    <AppSelect aria-label={w(`نوع الحد الأقصى ${roleName}`, `${roleName} team limit type`)} value={mode} onChange={next => {
      if (next === "all" && !allAvailable) return;
      setMode(next); if (next === "all") onChange(count!);
    }}>
      <option value="number">{w("عدد محدد", "Specific number")}</option>
      <option value="all" disabled={!allAvailable}>{w("كل الفرق المشاركة حاليًا", "All current participating teams")}</option>
    </AppSelect>
    {mode === "number" ? <input aria-label={w(`عدد أصوات ${roleName}`, `${roleName} vote allowance`)} type="number" required min={1} max={100} step={1} value={value} onChange={e => onChange(Number(e.target.value))} />
      : <p className="evaluation-all-count" role="status">{w(`${count} فرق مشاركة`, `${count} participating teams`)}</p>}
    <small>{mode === "all" ? w("يُحفظ العدد الحالي كحد أقصى؛ أعد ضبطه إذا تغيّرت الفرق قبل تجهيز الجولة.", "The current count is saved as the cap; update it if teams change before the round is prepared.") : w("لكل مقيّم في الجولة", "Per evaluator in this round")}</small>
  </div>;
}
